/**
 * Picking and queueing in a Discord bot league from the site. The bot does the work (the same rules, timers, Discord
 * announcements and spreadsheet updates as /pick, /makeuppick and /queuepick), as the player signed in with Discord.
 */
import type { PokemonData } from '@ogreclan/pokemon-core';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { api, type BotLeague, type BotPlayer, type BotPool, type BotPrices, type BotQueue, type BotTierList } from '../api';
import { ActionButton, Loading, toast } from '../ui';
import { BotMon } from './discord-league';

const base = (league: BotLeague, pool: BotPool) =>
	`/api/bot-leagues/${league.id}/pools/${encodeURIComponent(pool.label)}`;

/** The league's price list, fetched again when the board changes */
export function usePrices(league: BotLeague | null, pool: BotPool | null | undefined) {
	const [prices, setPrices] = useState<BotPrices | null>(null);
	const picks = pool?.picks.length ?? 0;
	useEffect(() => {
		if (!league || !pool || !['setup', 'active'].includes(pool.status)) return;
		let stopped = false;
		api.get<BotPrices>(`${base(league, pool)}/prices`).then(loaded => !stopped && setPrices(loaded),
			() => !stopped && setPrices({ available: false, entries: [] }));
		return () => {
			stopped = true;
		};
	}, [league?.id, pool?.label, pool?.status, picks]);
	return prices;
}

interface PanelProps {
	league: BotLeague;
	pool: BotPool;
	me: BotPlayer;
	data: PokemonData | null;
	prices: BotPrices | null;
	/** Loads the league again (after a pick) */
	reload: () => void;
	/** Staff picking for whoever is on the clock (me), rather than a player picking for themselves */
	staff?: boolean;
}

/** Your pick, on your turn or for a round you're owed */
export function BotPickPanel({ league, pool, me, data, prices, reload, staff }: PanelProps) {
	const turns = [
		...pool.onTheClock === me.id ? [{ round: pool.round, makeup: false }] : [],
		...staff ? [] : me.owedRounds.map(round => ({ round, makeup: true })),
	];
	const [turnIndex, setTurnIndex] = useState(0);
	const [search, setSearch] = useState('');
	const [onlyAffordable, setOnlyAffordable] = useState(true);
	const [confirming, setConfirming] = useState('');
	const [typed, setTyped] = useState({ name: '', cost: '', tera: false });
	const turn = turns[Math.min(turnIndex, turns.length - 1)];
	const tera = league.teraBudget > 0;

	const rows = useMemo(() => (prices?.entries || []).map(entry => ({
		...entry,
		problem: entry.cost > me.budgetRemaining ? `You have ${me.budgetRemaining} points left` : '',
		teraProblem: entry.tera_cost === null ? 'Not allowed as a Tera Captain' :
			entry.tera_cost > me.budgetRemaining || entry.tera_cost > me.teraBudgetRemaining ? `Not enough points or Tera points` : '',
	})), [prices, me]);
	if (!turn) return null;
	const query = search.trim().toLowerCase();
	const shown = rows.filter(row => (!onlyAffordable || !row.problem) && (!query || row.name.toLowerCase().includes(query)));

	const { round, makeup } = turn;
	async function pick(pokemon: string, cost: number | null, teraCaptain: boolean) {
		try {
			const result = await api.post<{ pokemon: string }>(`${base(league, pool)}/${staff ? 'staff/pick' : 'pick'}`, {
				pokemon, cost, teraCaptain, ...makeup ? { makeupRound: round } : { round },
			});
			toast(`${staff ? `${me.name} drafted` : 'You drafted'} ${result.pokemon}${teraCaptain ? ' as a Tera Captain' : ''}.`, 'good');
			setConfirming('');
			setTyped({ name: '', cost: '', tera: false });
			reload();
		} catch (e) {
			toast((e as Error).message, 'bad');
		}
	}

	return <div class="card">
		<h2 style="margin-top:0">{staff ? `Staff: pick for ${me.name}` : turn.makeup ? `Make up your round ${turn.round} pick` : `It's your pick`}</h2>
		{staff && <p class="small muted">As an admin or draft moderator. The bot posts that the pick was made for them on draft.ogreclan.com.</p>}
		<p class="muted small">
			{me.budgetRemaining} points left{tera && <> · {me.teraBudgetRemaining} Tera points</>} ·{' '}
			{Math.max(0, league.maxPicks - me.roster.length)} Pokémon to go · the bot posts it on Discord and the spreadsheet as usual
		</p>
		{turns.length > 1 && <div class="row" style="margin-bottom:8px">
			<span class="small">Picking for</span>
			<select value={turnIndex} onChange={e => setTurnIndex(Number((e.target as HTMLSelectElement).value))}>
				{turns.map((t, i) => <option key={i} value={i}>Round {t.round}{t.makeup ? ' (owed)' : ' (your turn)'}</option>)}
			</select>
		</div>}
		{prices?.available ? <>
			<div class="row" style="margin-bottom:8px">
				<input type="search" placeholder="Search" value={search} onInput={e => setSearch((e.target as HTMLInputElement).value)}
					style="flex:1;min-width:200px" />
				<label class="row small"><input type="checkbox" checked={onlyAffordable}
					onChange={e => setOnlyAffordable((e.target as HTMLInputElement).checked)} /> Only ones I can afford</label>
			</div>
			<div class="picker-list"><table>
				<thead><tr><th>Pokémon</th><th class="num">Cost</th>{tera && <th class="num">Tera</th>}<th /></tr></thead>
				<tbody>
				{shown.slice(0, 300).map(row => <tr key={row.name} class={row.problem ? 'cant' : ''} title={row.problem}>
					<td><BotMon data={data} name={row.name} /></td>
					<td class="num">{row.cost}</td>
					{tera && <td class="num muted small" title="As a Tera Captain">{row.tera_cost ?? '—'}</td>}
					<td class="right nowrap">{confirming === row.name ? <>
						<ActionButton class="primary small" action={() => pick(row.name, row.cost, false)}>Draft for {row.cost}</ActionButton>{' '}
						{tera && !row.teraProblem && <><ActionButton class="small" action={() => pick(row.name, row.tera_cost, true)}>
							Tera Captain for {row.tera_cost}</ActionButton>{' '}</>}
						<button type="button" class="small" onClick={() => setConfirming('')}>Cancel</button>
					</> : <button type="button" class="small" disabled={!!row.problem} onClick={() => setConfirming(row.name)}>Pick</button>}</td>
				</tr>)}
				{!shown.length && <tr><td class="empty">Nothing matches.</td></tr>}
			</tbody></table></div>
		</> : prices && <form class="row" onSubmit={e => {
			e.preventDefault();
			void pick(typed.name, typed.cost ? Number(typed.cost) : null, typed.tera);
		}}>
			<span class="muted small" style="width:100%">The price list can't be read right now, so type the Pokémon and its cost.</span>
			<input placeholder="Pokémon" value={typed.name} required onInput={e => setTyped({ ...typed, name: (e.target as HTMLInputElement).value })} />
			<input type="number" min="0" placeholder="Cost" value={typed.cost} required style="width:90px"
				onInput={e => setTyped({ ...typed, cost: (e.target as HTMLInputElement).value })} />
			{tera && <label class="row small"><input type="checkbox" checked={typed.tera}
				onChange={e => setTyped({ ...typed, tera: (e.target as HTMLInputElement).checked })} /> Tera Captain</label>}
			<button type="submit" class="primary">Draft it</button>
		</form>}
	</div>;
}

type Entry = { pokemon: string, tera: boolean };

/** Your pick queues: up to five Pokémon per round, drafted in order when your turn comes (only you can see them) */
export function BotQueuePanel({ league, pool, me, prices }: PanelProps) {
	const [queue, setQueue] = useState<BotQueue | null>(null);
	const [error, setError] = useState('');
	const [round, setRound] = useState(0);
	const [draft, setDraft] = useState<Entry[]>([]);
	const [adding, setAdding] = useState('');
	const tera = league.teraBudget > 0;
	const picks = pool.picks.length;

	const load = async () => {
		try {
			setQueue(await api.get<BotQueue>(`${base(league, pool)}/queue`));
			setError('');
		} catch (e) {
			setError((e as Error).message);
		}
	};
	useEffect(() => {
		void load();
	}, [league.id, pool.label, picks]);

	// the rounds still to come for you
	const yourTurnDone = pool.picks.some(p => p.playerId === me.id && p.round === pool.round) || (pool.status === 'active' &&
		pool.onTheClock !== me.id && seatPassed(pool, me));
	const first = pool.status === 'active' ? pool.round + (yourTurnDone ? 1 : 0) : 1;
	const upcoming = Array.from({ length: Math.max(0, league.maxPicks - first + 1) }, (_, i) => first + i)
		.filter(r => !queue?.picked.includes(r));
	const chosen = upcoming.includes(round) ? round : upcoming[0] ?? 0;
	const saved = queue?.rounds.find(r => r.round === chosen)?.entries || [];
	const savedKey = JSON.stringify(saved.map(e => [e.pokemon, e.tera_captain]));
	useEffect(() => {
		setDraft(saved.map(e => ({ pokemon: e.pokemon, tera: e.tera_captain })));
	}, [chosen, savedKey]);

	if (error) return <div class="card"><h2 style="margin-top:0">Your pick queue</h2><p class="notice bad">{error}</p></div>;
	if (!queue || !upcoming.length) return null;
	const changed = JSON.stringify(draft.map(e => [e.pokemon, e.tera])) !== savedKey;
	const move = (from: number, to: number) => {
		const next = [...draft];
		next.splice(to, 0, ...next.splice(from, 1));
		setDraft(next);
	};

	return <div class="card">
		<h2 style="margin-top:0">Your pick queue</h2>
		<p class="muted small">If your turn comes and you're away, the bot drafts the first of these that's still available and
			that you can afford, exactly like /queuepick. Only you can see your queue.</p>
		{queue.rounds.length > 0 && <ul class="history small">{queue.rounds.map(r => <li key={r.round}>
			<strong>Round {r.round}</strong>: {r.entries.map(e => `${e.pokemon}${e.tera_captain ? ' (Tera)' : ''}`).join(', ')}
		</li>)}</ul>}
		<div class="row" style="margin:8px 0">
			<span class="small">Round</span>
			<select value={chosen} onChange={e => setRound(Number((e.target as HTMLSelectElement).value))}>
				{upcoming.map(r => <option key={r} value={r}>Round {r}{queue.rounds.some(q => q.round === r) ? ' (queued)' : ''}</option>)}
			</select>
		</div>
		<ol class="queue">
			{draft.map((entry, i) => <li key={`${entry.pokemon}-${i}`} class="row">
				<span style="flex:1">{entry.pokemon}</span>
				{tera && <label class="row small"><input type="checkbox" checked={entry.tera} onChange={e => {
					const next = [...draft];
					next[i] = { ...entry, tera: (e.target as HTMLInputElement).checked };
					setDraft(next);
				}} /> Tera</label>}
				<button type="button" class="small" disabled={i === 0} title="Move up" onClick={() => move(i, i - 1)}>↑</button>
				<button type="button" class="small" disabled={i === draft.length - 1} title="Move down" onClick={() => move(i, i + 1)}>↓</button>
				<button type="button" class="small" title="Remove" onClick={() => setDraft(draft.filter((_, j) => j !== i))}>✕</button>
			</li>)}
		</ol>
		{!draft.length && <p class="empty">Nothing queued for round {chosen}.</p>}
		{draft.length < 5 && <form class="row" onSubmit={e => {
			e.preventDefault();
			const name = adding.trim();
			if (!name || draft.some(entry => entry.pokemon.toLowerCase() === name.toLowerCase())) return;
			setDraft([...draft, { pokemon: name, tera: false }]);
			setAdding('');
		}}>
			<input list="bot-queue-names" placeholder="Add a Pokémon" value={adding} onInput={e => setAdding((e.target as HTMLInputElement).value)} />
			<datalist id="bot-queue-names">{prices?.entries.map(entry => <option key={entry.name} value={entry.name}>{entry.cost} pts</option>)}</datalist>
			<button type="submit" class="small">Add</button>
		</form>}
		<div class="row" style="margin-top:10px">
			<ActionButton class="primary" disabled={!changed || !draft.length} action={async () => {
				try {
					await api.put(`${base(league, pool)}/queue`, { round: chosen, pokemon: draft.map(e => e.pokemon), tera: draft.map(e => e.tera) });
					toast(`Saved your round ${chosen} queue.`, 'good');
					await load();
				} catch (e) {
					toast((e as Error).message, 'bad');
				}
			}}>Save round {chosen}</ActionButton>
			<ActionButton disabled={!saved.length} confirm={`Clear your round ${chosen} queue?`} action={async () => {
				try {
					await api.delete(`${base(league, pool)}/queue?round=${chosen}`);
					toast(`Cleared your round ${chosen} queue.`);
					await load();
				} catch (e) {
					toast((e as Error).message, 'bad');
				}
			}}>Clear</ActionButton>
		</div>
	</div>;
}

/** Whether your turn in the current round has already gone by (picked or skipped) */
function seatPassed(pool: BotPool, me: BotPlayer) {
	return pool.skips.some(s => s.playerId === me.id && s.round === pool.round);
}

/** The pool's tiering list, grouped by cost: what's still there, who has the rest, and what's banned */
export function BotTierList({ league, pool, data }: { league: BotLeague, pool: BotPool, data: PokemonData | null }) {
	const [list, setList] = useState<BotTierList | null>(null);
	const [error, setError] = useState('');
	const [search, setSearch] = useState('');
	const [hideTaken, setHideTaken] = useState(false);
	const [showBanned, setShowBanned] = useState(false);
	const taken = pool.players.reduce((n, p) => n + p.roster.length, 0);
	useEffect(() => {
		let stopped = false;
		api.get<BotTierList>(`${base(league, pool)}/tierlist`).then(loaded => {
			if (stopped) return;
			setList(loaded);
			setError('');
		}, e => !stopped && setError((e as Error).message));
		return () => {
			stopped = true;
		};
	}, [league.id, pool.label, pool.picks.length, taken]);

	if (error) return <p class="notice bad">{error}</p>;
	if (!list) return <Loading />;
	if (!list.available) {
		return <p class="notice">The tier list isn't available right now: the draft bot couldn't read it. Try again in a minute.</p>;
	}
	const owner = (id: number | null) => pool.players.find(p => p.id === id);
	const query = search.trim().toLowerCase();
	const shown = list.entries.filter(e => (!query || e.name.toLowerCase().includes(query)) && !(hideTaken && e.taken_by !== null));
	const tiers = [...new Set(shown.filter(e => !e.banned).map(e => e.cost ?? 0))].sort((a, b) => b - a);
	const banned = shown.filter(e => e.banned);
	const left = list.entries.filter(e => !e.banned && e.taken_by === null).length;
	const tera = league.teraBudget > 0;

	const card = (entry: BotTierList['entries'][number]) => {
		const who = owner(entry.taken_by);
		return <li key={entry.name} class={`tier-mon ${entry.taken_by !== null ? 'taken' : ''} ${entry.taken_by !== null && entry.taken_by === pool.you ? 'mine' : ''}`}
			title={who ? `${entry.name}: ${who.name}` : entry.name}>
			<BotMon data={data} name={entry.name} />
			<span class="small">
				{who ? <span class="badge">{who.name}</span> : entry.taken_by !== null ? <span class="badge">taken</span> : null}
				{tera && !entry.banned && <span class="muted" title="As a Tera Captain">{entry.tera_banned ? ' no Tera' : entry.tera_cost !== null ? ` Tera ${entry.tera_cost}` : ''}</span>}
			</span>
		</li>;
	};

	return <div class="card">
		<div class="row" style="justify-content:space-between">
			<h2 style="margin:0">Tier list{league.pools.length > 1 && `: pool ${pool.label}`}</h2>
			<span class="muted small">{left} of {list.entries.filter(e => !e.banned).length} still available</span>
		</div>
		<div class="row" style="margin:10px 0">
			<input type="search" placeholder="Search" value={search} onInput={e => setSearch((e.target as HTMLInputElement).value)} style="flex:1;min-width:180px" />
			<label class="row small"><input type="checkbox" checked={hideTaken} onChange={e => setHideTaken((e.target as HTMLInputElement).checked)} /> Hide taken</label>
		</div>
		{tiers.map(cost => <section key={cost} class="tier">
			<h3>{cost} point{cost === 1 ? '' : 's'}</h3>
			<ul class="tier-list">{shown.filter(e => !e.banned && (e.cost ?? 0) === cost).map(card)}</ul>
		</section>)}
		{banned.length > 0 && <section class="tier">
			<h3>Banned ({banned.length}){' '}
				<button type="button" class="small" onClick={() => setShowBanned(!showBanned)}>{showBanned ? 'Hide' : 'Show'}</button></h3>
			{(showBanned || query) && <ul class="tier-list">{banned.slice(0, query ? 60 : banned.length).map(card)}</ul>}
		</section>}
		{!shown.length && <p class="empty">Nothing matches.</p>}
	</div>;
}

/** Staff: change any player's pick, or fill in a round they have none for (as a moderator's /editpick) */
export function BotEditPick({ league, pool, prices, chosen, reload }: {
	league: BotLeague, pool: BotPool, prices: BotPrices | null, chosen: { playerId: number, round: number } | null, reload: () => void,
}) {
	const [playerId, setPlayerId] = useState(0);
	const [round, setRound] = useState(1);
	const [pokemon, setPokemon] = useState('');
	const [cost, setCost] = useState('');
	const [tera, setTera] = useState(false);
	useEffect(() => {
		if (!chosen) return;
		setPlayerId(chosen.playerId);
		setRound(chosen.round);
	}, [chosen]);
	const player = pool.players.find(p => p.id === playerId);
	const current = pool.picks.find(p => p.playerId === playerId && p.round === round);

	return <div class="card">
		<h3 style="margin-top:0">Staff: edit a pick</h3>
		<p class="small muted">Change anyone's pick, or fill in a round they missed, as a moderator's /editpick does. Click a square on
			the board to choose it. Leave the cost blank to use the tier list's.</p>
		<form class="row" onSubmit={async e => {
			e.preventDefault();
			try {
				const result = await api.post<{ created: boolean, pokemon: string, cost: number }>(`${base(league, pool)}/staff/editpick`, {
					playerId, round, pokemon, cost: cost ? Number(cost) : null, teraCaptain: tera,
				});
				toast(`${result.created ? 'Recorded' : 'Changed'} ${player?.name}'s round ${round} pick: ${result.pokemon} (${result.cost}).`, 'good');
				setPokemon('');
				setCost('');
				setTera(false);
				reload();
			} catch (error) {
				toast((error as Error).message, 'bad');
			}
		}}>
			<select value={playerId} required onChange={e => setPlayerId(Number((e.target as HTMLSelectElement).value))}>
				<option value={0}>Whose pick…</option>
				{[...pool.players].sort((a, b) => a.seat - b.seat).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
			</select>
			<select value={round} onChange={e => setRound(Number((e.target as HTMLSelectElement).value))}>
				{Array.from({ length: league.maxPicks }, (_, i) => i + 1).map(r => <option key={r} value={r}>Round {r}</option>)}
			</select>
			<input list="staff-edit-names" placeholder={current ? `Instead of ${current.pokemon}` : 'Pokémon'} value={pokemon} required
				onInput={e => setPokemon((e.target as HTMLInputElement).value)} />
			<datalist id="staff-edit-names">{prices?.entries.map(entry => <option key={entry.name} value={entry.name}>{entry.cost} pts</option>)}</datalist>
			<input type="number" min="0" placeholder="Cost" value={cost} style="width:80px" onInput={e => setCost((e.target as HTMLInputElement).value)} />
			{league.teraBudget > 0 && <label class="row small"><input type="checkbox" checked={tera}
				onChange={e => setTera((e.target as HTMLInputElement).checked)} /> Tera Captain</label>}
			<button type="submit" class="primary" disabled={!playerId}>{current ? 'Change the pick' : 'Record the pick'}</button>
		</form>
		{player && <p class="small muted">{player.name}, round {round}: {current ? `${current.pokemon} (${current.cost})` : 'no pick yet'}.</p>}
	</div>;
}
